import React, { useCallback, useEffect, useState, useMemo } from "react";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import styled, { withTheme } from "styled-components";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import moment from "moment";
import { DEFAULT_LOCALE, getLanguages } from "../../../i18n/helpers";

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const StyledContainerHorizontal = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledLabel = styled.label`
  font-weight: 600;
  font-size: 16px;
`;

const yearRegex = new RegExp("^[0-9]{1,4}$");

const minDate = 1900;
const presentYear = new Date().getFullYear();

const EditParticipant = (props) => {
  const {
    onSubmit,
    sites,
    chosenSite,
    participantDetails,
    participant,
    studyId,
    loading,
  } = props;

  const [errors, setErrors] = useState({});

  const [ugnxId, setUgnxId] = useState("");
  const [yearOfBirth, setYearOfBirth] = useState("");
  const [site, setSite] = useState("");
  const [reasonForChange, setreasonForChange] = useState("");
  const [validReasonForChange, setValidReasonForChange] = useState(false);
  const [language, setLanguage] = useState("");

  const availableLanguages = useMemo(
    () => getLanguages().filter((language) => language.isAvailable),
    []
  );

  useEffect(() => {
    setUgnxId(participant?.userName);
    setYearOfBirth(participantDetails?.yearOfBirth);
    setLanguage(participantDetails?.language || DEFAULT_LOCALE);
    setSite(participant?.siteId);
  }, [participant, participantDetails]);

  const handleYearOfBirthDateChange = (value) => {
    if (value === "" || (value && yearRegex.test(value))) {
      setYearOfBirth(value);
    }
  };

  const validateYearBirth = () => {
    setErrors({
      ...errors,
      yearOfBirth: isYearBirthValid()
        ? undefined
        : `The date must be between ${minDate} and ${presentYear}`,
    });
  };

  const validateReason = () => {
    let reasonError;

    if (reasonForChange.replace(/\.|,/g, "").trim() === "") {
      reasonError = "Please provide a reason";
    } else if (reasonForChange.length > 450) {
      reasonError = "Please enter a reason with less than 450 characters";
    }
    setErrors({
      ...errors,
      reason: reasonError,
    });
  };

  const isYearBirthValid = useCallback(() => {
    if (!yearOfBirth || yearOfBirth < minDate || yearOfBirth > presentYear) {
      return false;
    }
    return true;
  }, [yearOfBirth]);

  const handleSiteOnChange = (value) => {
    const siteFound = sites?.find((site) => site.name === value);
    setSite(siteFound.key);
  };

  const getSiteNames = useMemo(() => sites?.map((site) => site.name), [sites]);

  useEffect(() => {
    setValidReasonForChange(
      reasonForChange.replace(/\.|,/g, "").trim() !== "" &&
        reasonForChange.length <= 450
    );
  }, [reasonForChange]);

  const getChosenSite = useMemo(
    () => sites?.find((site) => site.key === chosenSite.siteId),
    [chosenSite, sites]
  );

  const checkErrors = useCallback(() => {
    setErrors((prevState) => ({
      ...prevState,
      yearOfBirth: isYearBirthValid()
        ? undefined
        : `The date must be between ${minDate} and ${new Date().getFullYear()}`,
    }));
  }, [isYearBirthValid]);

  const validateBeforeSubmit = (event) => {
    event.preventDefault();
    checkErrors();
    if (isYearBirthValid() && reasonForChange !== "") {
      submitForm();
    }
  };

  const getYearOfBirth = useCallback(() => {
    if (isYearBirthValid()) {
      return `${moment(yearOfBirth).get("year")}`;
    }

    return yearOfBirth;
  }, [isYearBirthValid, yearOfBirth]);

  const submitForm = useCallback(() => {
    const { participantId, studyStatusId } = participant;

    const formData = {
      participantId,
      newSiteId: site,
      studyId,
      studyStatus: studyStatusId,
      ugnxId,
      yearOfBirth: parseInt(getYearOfBirth()),
      reasonForChange,
      oldSiteId: participant.siteId,
      language,
    };

    onSubmit(formData);
  }, [
    onSubmit,
    ugnxId,
    studyId,
    participant,
    reasonForChange,
    getYearOfBirth,
    site,
    language,
  ]);

  return (
    <FormContainer data-testid="editparticipant">
      <LabelledInput
        data-testid="ugnxID-input"
        title="Participant ID:"
        value={ugnxId}
        type="text"
        placeholder="XXX-XXX"
        disabled
      />
      <LabelledInput
        data-testid="yearOfBirth-input"
        className="mt-4"
        title="Year of birth:"
        min={minDate}
        max={presentYear}
        value={getYearOfBirth() || ""}
        type="text"
        placeholder="YYYY"
        pattern="[0-9]*"
        hasError={!!errors?.yearOfBirth}
        errorMessage={errors?.yearOfBirth}
        onChange={(e) => {
          e.target.validity.valid &&
            handleYearOfBirthDateChange(e.target.value);
        }}
        onBlur={() => validateYearBirth()}
        autoFocus={true}
      />

      <StyledContainerHorizontal className="mt-4">
        <StyledLabel className="mb-0 mr-2">Site:</StyledLabel>
        <CustomDropdown
          placeholder={getChosenSite ? getChosenSite.name : "Select"}
          values={getSiteNames}
          limitheight
          onChange={(e) => handleSiteOnChange(e)}
          width={145}
        />
      </StyledContainerHorizontal>

      <StyledContainerHorizontal className="mt-4">
        <StyledLabel className="mb-0 mr-2">Select language:</StyledLabel>
        <CustomDropdown
          placeholder={
            getLanguages().find((lang) => lang.code === language)?.value ??
            "Select"
          }
          values={availableLanguages}
          width={145}
          onChange={(lang) => setLanguage(lang.code)}
        />
      </StyledContainerHorizontal>

      <LabelledInput
        title="Reason for change:"
        className="mt-4"
        type="text"
        data-testid="update-reason-input"
        hasError={!!errors.reason}
        errorMessage={errors.reason}
        value={reasonForChange}
        onChange={(e) => setreasonForChange(e.target.value)}
        onBlur={() => validateReason()}
      />
      <StyledFooter>
        <PrimaryButton
          data-testid="submitparticipant"
          onClick={(e) => validateBeforeSubmit(e)}
          disabled={!validReasonForChange || loading}
        >
          Save
        </PrimaryButton>
      </StyledFooter>
    </FormContainer>
  );
};

export default withTheme(EditParticipant);
